import PriceData from '../../model/price-data';
import WarehouseStockData from '../../model/warehouse-stock-data';
import StockAvailability from '../shared/stock-availability';
import StockAndPriceData from '../../model/stock-and-price-data';
import { formatPrice } from '../../helpers/utils';
import MessageHelper from '../../helpers/message-helper';
import DeliveryAvailability from './delivery-availability';
import StockForecast from '../../model/forecast-stock-data';
import ProductData from '../../model/product-data';

type AsyncDataKey =
  keyof StockAndPriceData
  | typeof StockAvailability.ASYNC_KEY
  | typeof DeliveryAvailability.ASYNC_KEY
  | 'price'
  | 'priceExclVat'
  | 'stockCustomerLabel';

export default class AsyncProductData {
  static readonly DATA_LOADED_EVENT = 'asyncDataLoaded';

  private readonly refreshButton: JQuery<HTMLButtonElement>;
  private requestInProgress = false;
  private requestChunkSize = 20;
  private static productsEndpoint = '/products/stockPrice';

  constructor(private readonly rows: JQuery = $('.js-asyncProductData')) {
    this.refreshButton = $('.js-asyncProductDataRefresh');
    if (this.rows.length) {
      this.loadAsyncProductData();
    }
    this.bindRefreshButton();
  }

  static init(container: JQuery): AsyncProductData {
    return new AsyncProductData(container.find('.js-asyncProductData'));
  }

  static initAll(path?: string): AsyncProductData {
    if (path) this.productsEndpoint = path;
    return new AsyncProductData();
  }

  private static dispatchDataLoadedEvent(): void {
    document.body.dispatchEvent(new Event(AsyncProductData.DATA_LOADED_EVENT));
  }

  static async refetchNewDataAndCalculations(productCode: string, quantity: string): Promise<void> {
    const field = document.querySelector(`[data-async-key='deliveryAvailability'][data-product-code='${productCode}']`) as HTMLElement;
    if (field) {
      // eslint-disable-next-line
      const [firstProduct]: ProductData[] = await this.fetchAsyncProductData([{
        productCode,
        quantity,
      }] as Array<{ productCode: string; quantity: string }>);

      AsyncProductData.renderDeliveryAvailability($(field), firstProduct.stockForecasts);
    }
  }

  private bindRefreshButton(): void {
    this.refreshButton.on('click', () => {
      this.loadAsyncProductData();
    });
  }

  private loadAsyncProductData(): void {
    if (this.requestInProgress) {
      return;
    }
    const scrapedRows: Record<string, string>[] = this.scrapeCodesFromRows();
    const productsWithoutVariants: Record<string, string>[] = scrapedRows.filter((item: Record<string, string>) => !item.productVariantCodes);
    const productsWithVariants: Record<string, string>[] = scrapedRows.filter((item: Record<string, string>) => item.productVariantCodes);

    this.refreshButton.addClass('-icon-animated');
    this.requestInProgress = true;

    const regularProductRequest: Promise<void> = this.loadAsyncProductWithoutVariantsData(productsWithoutVariants);
    const variantProductRequests: Promise<void>[] = productsWithVariants.map(
      (productWithVariants: Record<string, string>) => this.loadAsyncProductVariantsData(productWithVariants),
    );

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    Promise.all([regularProductRequest, ...variantProductRequests]).then((): void => {
      AsyncProductData.removePriceLoader();
      AsyncProductData.dispatchDataLoadedEvent();
      this.refreshButton.removeClass('-icon-animated');
    });
  }

  private async loadAsyncProductWithoutVariantsData(products: Record<string, string>[]): Promise<void> {
    if (products.length > this.requestChunkSize) {
      const allChunksArray: any[] = [];

      for (let i = 0; i < products.length; i += this.requestChunkSize) {
        allChunksArray.push(products.slice(i, i + this.requestChunkSize));
      }

      const result = await Promise.all(allChunksArray.map(
        (chunk) => AsyncProductData.fetchAsyncProductData(chunk).done((data: StockAndPriceData[]) => {
          data.forEach((stockAndPriceData: StockAndPriceData): void => {
            const row: JQuery<HTMLElement> = this.rows.filter(`[data-code="${stockAndPriceData.productCode}"]`);
            AsyncProductData.populateFields(row, stockAndPriceData);
          });
        }).fail((error) => this.loadAsyncProductDataError(error)),
      ));

      return Promise.resolve(result).then();
    }

    return AsyncProductData.fetchAsyncProductData(products).done((data: StockAndPriceData[]) => {
      data.forEach((stockAndPriceData: StockAndPriceData): void => {
        const row: JQuery<HTMLElement> = this.rows.filter(`[data-code="${stockAndPriceData.productCode}"]`);
        AsyncProductData.populateFields(row, stockAndPriceData);
      });
    }).fail((error) => this.loadAsyncProductDataError(error));
  }

  private async loadAsyncProductVariantsData(product: Record<string, string>): Promise<void> {
    const variantCodes: Record<string, string>[] = [];
    const row: JQuery<HTMLElement> = this.rows.filter(`[data-code="${product.productCode}"]`);
    product.productVariantCodes.split(',').forEach((productCode: string) => variantCodes.push({ productCode }));

    if (variantCodes.length > this.requestChunkSize) {
      const allChunksArray: any[] = [];

      for (let i = 0; i < variantCodes.length; i += this.requestChunkSize) {
        allChunksArray.push(variantCodes.slice(i, i + this.requestChunkSize));
      }

      const result = await Promise.all(allChunksArray.map(
        (chunk) => AsyncProductData.fetchAsyncProductData(chunk).done((data: StockAndPriceData[]) => {
          const variantsWithPrice: StockAndPriceData[] = data.filter((item: StockAndPriceData) => item.sapPrice);
          const variantsWithPromotionPrice: StockAndPriceData[] = data.filter((item: StockAndPriceData) => item.sapPromotionPrice);

          if (variantsWithPromotionPrice.length) {
            AsyncProductData.populateFields(row, variantsWithPromotionPrice.sort(
              (a: StockAndPriceData, b: StockAndPriceData) => a.sapPromotionPrice.value - b.sapPromotionPrice.value,
            ).shift() as StockAndPriceData);
            return;
          }

          if (variantsWithPrice.length) {
            AsyncProductData.populateFields(row, variantsWithPrice.sort(
              (a: StockAndPriceData, b: StockAndPriceData) => a.sapPrice.value - b.sapPrice.value,
            ).shift() as StockAndPriceData);
            return;
          }

          AsyncProductData.populateFields(row, data[0]);
        }).fail((error) => this.loadAsyncProductDataError(error)),
      ));

      return Promise.resolve(result).then();
    }

    return AsyncProductData.fetchAsyncProductData(variantCodes).done((data: StockAndPriceData[]) => {
      const variantsWithPrice: StockAndPriceData[] = data.filter((item: StockAndPriceData) => item.sapPrice);
      const variantsWithPromotionPrice: StockAndPriceData[] = data.filter((item: StockAndPriceData) => item.sapPromotionPrice);

      if (variantsWithPromotionPrice.length) {
        AsyncProductData.populateFields(row, variantsWithPromotionPrice.sort(
          (a: StockAndPriceData, b: StockAndPriceData) => a.sapPromotionPrice.value - b.sapPromotionPrice.value,
        ).shift() as StockAndPriceData);
        return;
      }

      if (variantsWithPrice.length) {
        AsyncProductData.populateFields(row, variantsWithPrice.sort(
          (a: StockAndPriceData, b: StockAndPriceData) => a.sapPrice.value - b.sapPrice.value,
        ).shift() as StockAndPriceData);
        return;
      }

      AsyncProductData.populateFields(row, data[0]);
    }).fail((error) => this.loadAsyncProductDataError(error));
  }

  private loadAsyncProductDataError(error: JQuery.jqXHR<any>): void {
    this.requestInProgress = false;
    this.refreshButton.removeClass('-icon-animated');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (error.responseJSON?.length) {
      window.NotificationHelper.clearNotifications();
      window.NotificationHelper.showError(error.responseJSON);
    }
  }

  private scrapeCodesFromRows(): Record<string, string>[] {
    const codes: Record<string, string>[] = [];
    this.rows.each((index: number, element: HTMLElement): void => {
      const productCode: string = $(element).data('code') as string;
      const productVariantCodes: string = $(element).data('product-variant-codes') as string;
      codes.push({
        productCode: productCode.toString(),
        productVariantCodes,
      });
    });
    return codes;
  }

  private static fetchAsyncProductData(data: Record<string, string>[]): JQuery.jqXHR {
    return $.ajax({
      url: `${window.ACC.config.encodedContextPath}${this.productsEndpoint}`,
      type: 'POST',
      contentType: 'application/json; charset=utf-8',
      dataType: 'json',
      data: JSON.stringify(data),
    });
  }

  private static populateFields(row: JQuery, data: StockAndPriceData): void {
    row.find('.js-asyncField').each((index, fieldEl) => {
      const field = $(fieldEl);
      const key = String(field.data('async-key') ?? '') as AsyncDataKey;

      switch (key) {
        case StockAvailability.ASYNC_KEY:
          AsyncProductData.renderStockAvailability(field, data.warehouses?.filter((warehouseStock) => warehouseStock.type !== 'CUSTOMER'));
          break;
        case DeliveryAvailability.ASYNC_KEY:
          AsyncProductData.renderDeliveryAvailability(field, data.stockForecasts);
          break;
        case 'price':
          AsyncProductData.renderPriceValues(field, data.sapPriceWithVat, data.sapPromotionPriceWithVat);
          break;
        case 'priceExclVat':
          AsyncProductData.renderPriceValues(field, data.sapPrice, data.sapPromotionPrice);
          break;
        case 'stockCustomerLabel':
          AsyncProductData.renderStockCustomerLabel(field, data.warehouses);
          break;
        default:
          AsyncProductData.renderSimpleDataType(field, data, key);
      }
    });
  }

  private static renderStockAvailability(element: JQuery<HTMLElement>, data: WarehouseStockData[]): StockAvailability | undefined {
    try {
      return new StockAvailability(element[0] as HTMLDivElement, data);
    } catch (e: unknown) {
      if (e instanceof Error) {
        // eslint-disable-next-line no-console
        console.log(e.message);
      }
      return undefined;
    }
  }

  private static renderDeliveryAvailability(element: JQuery<HTMLElement>, data?: StockForecast[]) {
    try {
      const el = element[0];
      if (!el.classList.contains('is-model-based') && Boolean(data)) {
        return new DeliveryAvailability(el as HTMLDivElement, data);
      }
      if (el.classList.contains('is-model-based')) {
       return new DeliveryAvailability(el as HTMLDivElement);
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        // eslint-disable-next-line no-console
        console.log(e.message);
      }
      return undefined;
    }
  }

  private static renderPriceValues(element: JQuery<HTMLElement>, regularPrice: PriceData | null, promotionPrice: PriceData | null): void {
    const showCurrencyCode = element.data('currency-code') as boolean;

    if (!regularPrice) {
      return;
    }
    const regularPriceFormated = formatPrice(regularPrice, showCurrencyCode);

    if (promotionPrice) {
      const promotionPriceFormatted = formatPrice(promotionPrice, showCurrencyCode);
      element.html(`
        <span class="price-value -large -promotion">${promotionPriceFormatted}</span>
        <span class="price-value -crossed-out">${regularPriceFormated}</span>
      `);
    } else {
      element.html(`<span class="price-value -large">${regularPriceFormated}</span>`);
    }
    element.data('price-populated', true);
  }

  private static renderStockCustomerLabel(element: JQuery<HTMLElement>, warehouses: WarehouseStockData[]): void {
    if (!warehouses) {
      return;
    }
    const stockLevel = warehouses.find((property) => property.type === 'CUSTOMER')?.stockLevelAmount ?? 0;
    if (stockLevel > 0) {
      element.html(`<b>${stockLevel}</b> ${MessageHelper.getMessage('common.customer.stock')}`);
    }
  }

  private static renderSimpleDataType(element: JQuery<HTMLElement>, data: StockAndPriceData, key: AsyncDataKey): void {
    // legacy field name mapping
    if ((key as string) === 'sapPrice') {
      // eslint-disable-next-line no-param-reassign
      key = 'sapPriceWithVat';
    }
    if ((key as string) === 'sapPromotionPrice') {
      // eslint-disable-next-line no-param-reassign
      key = 'sapPromotionPriceWithVat';
    }

    const fieldValue: unknown = data[key];
    if (fieldValue === null || fieldValue === undefined || fieldValue === 'undefined') {
      return;
    }

    switch (key) {
      case 'sapPriceWithVat':
      case 'sapPromotionPriceWithVat': {
        const showCurrencyCode = element.data('currency-code') as boolean;
        element.html(`<span class="price-value -large">${formatPrice(fieldValue as PriceData, showCurrencyCode)}</span>`);
        break;
      }
      case 'warehouses': {
        const stockLevel = (fieldValue as WarehouseStockData[]).find((property) => property.type === 'CUSTOMER')?.stockLevelAmount ?? 0;
        if (stockLevel > 0) {
          element.text(stockLevel);
        }
        break;
      }
      default:
        element.text(fieldValue as string | number);
        break;
    }
  }

  private static removePriceLoader(): void {
    $('[data-async-key="price"]').each((_, el) => {
      const element = $(el);
      const showCurrencyCode = element.data('currency-code') as boolean;
      if (element.data('price-populated') !== true) {
        element.html(`<span class="price-value -large">${showCurrencyCode ? window.ACC.currencyCode : ''}&nbsp--</span>`);
      }
    });
  }
}
